package com.arenaproplay.admin.state.auth

import androidx.compose.runtime.*
import com.arenaproplay.admin.data.auth.AuthTokens
import com.arenaproplay.admin.data.auth.AuthUser
import com.arenaproplay.admin.utils.Storage
import com.arenaproplay.admin.utils.StorageKeys
import kotlinx.serialization.json.Json

class AuthSessionState(
    initialTokens: AuthTokens?,
    initialUser: AuthUser?
) {
    var tokens by mutableStateOf(initialTokens)
        private set
    var user by mutableStateOf(initialUser)
        private set

    val isAuthenticated: Boolean get() = tokens != null && user != null
    val isAdmin: Boolean get() = user?.isAdmin == true

    fun signIn(tokens: AuthTokens, user: AuthUser) {
        this.tokens = tokens
        this.user = user
        Storage.save(StorageKeys.ACCESS_TOKEN, tokens.accessToken)
        Storage.save(StorageKeys.REFRESH_TOKEN, tokens.refreshToken)
        Storage.save(StorageKeys.CURRENT_USER, Json.encodeToString(AuthUser.serializer(), user))
    }

    fun updateAccessToken(accessToken: String) {
        val current = tokens ?: return
        val updated = current.copy(accessToken = accessToken)
        tokens = updated
        Storage.save(StorageKeys.ACCESS_TOKEN, accessToken)
    }

    /** Called by the HTTP layer after a successful refresh round-trip. */
    fun updateTokens(
        accessToken: String,
        refreshToken: String,
    ) {
        tokens = AuthTokens(accessToken, refreshToken)
        Storage.save(StorageKeys.ACCESS_TOKEN, accessToken)
        Storage.save(StorageKeys.REFRESH_TOKEN, refreshToken)
    }

    fun signOut() {
        tokens = null
        user = null
        Storage.remove(StorageKeys.ACCESS_TOKEN)
        Storage.remove(StorageKeys.REFRESH_TOKEN)
        Storage.remove(StorageKeys.CURRENT_USER)
    }
}

val LocalAuthSession = compositionLocalOf<AuthSessionState> {
    error("AuthSession not provided")
}

@Composable
fun rememberAuthSession(): AuthSessionState {
    return remember {
        val accessToken = Storage.get(StorageKeys.ACCESS_TOKEN)
        val refreshToken = Storage.get(StorageKeys.REFRESH_TOKEN)
        val userJson = Storage.get(StorageKeys.CURRENT_USER)

        val tokens = if (accessToken != null && refreshToken != null) {
            AuthTokens(accessToken = accessToken, refreshToken = refreshToken)
        } else null

        val user = userJson?.let {
            runCatching { Json.decodeFromString(AuthUser.serializer(), it) }.getOrNull()
        }

        AuthSessionState(tokens, user)
    }
}

@Composable
fun ProvideAuthSession(
    session: AuthSessionState = rememberAuthSession(),
    content: @Composable () -> Unit
) {
    CompositionLocalProvider(LocalAuthSession provides session) {
        content()
    }
}
